/**
 * Shared status → pill-variant mapping used by every list page.
 *
 * The four pill variants come from `features/backlog/backlog.css`:
 *   • ok    — green, success/done states          (Approved, Delivered, Sent, …)
 *   • warn  — amber, attention/in-progress        (Pending Approval, Waiting, …)
 *   • info  — blue,  neutral / informational      (Pending Validation, Pending Delivery, …)
 *   • alert — red,   failure / blocked            (Failed, Cancelled, …)
 *
 * Before this helper existed, Subout, Analysis, and Reporting each had their
 * own mapping — same status word could end up green on one form and amber on
 * another.  Centralising the rule here means "Approved" looks identical in
 * the Result Approval grid and the Report Tracking grid.
 *
 * Add new statuses here when a new SP starts emitting them; never patch a
 * per-page mapping again.
 */
export type StatusVariant = 'ok' | 'warn' | 'info' | 'alert';

/**
 * Returns the pill class variant for a status label.  Case- and
 * whitespace-insensitive; matches the longest applicable rule.
 */
export function statusVariant(label: string | null | undefined): StatusVariant {
  if (!label) return 'info';
  const s = label.trim().toLowerCase();

  // ── Success / done — green ───────────────────────────────────────────
  // "entered" is the DONE state of result entry (the QCBatchSequence enum runs
  // Pending Result Entry -> Pending Sample Prep -> Entered), so it is green
  // like every other terminal state. Added 2026-09-25 for the PLM sequence
  // rail, which showed it as unstyled plain text.
  if (/(^|\s)(approved|completed|delivered|sent|validated|exported|submitted|entered|pass(ed)?)$/i.test(s))
    return 'ok';
  if (/result completed|sdms validated|sample(d)?$/i.test(s))
    return 'ok';
  // QA/QC Training Records: a qualified analyst is a done/green state.
  if (/^qualified$/i.test(s))
    return 'ok';

  // ── Failure / blocked — red ──────────────────────────────────────────
  // MOR Adhoc QC Status emits the bare word "Fail" — match it alongside
  // the existing failure synonyms.
  if (/(^|\s)fail(ed)?$/i.test(s))
    return 'alert';
  if (/(failed|cancel(l)?ed|rejected|rolled back|out of spec|do not deliver)/i.test(s))
    return 'alert';

  // ── Pending / waiting — amber for "needs your attention" actions ─────
  // (Approval / signing / waiting on people is amber; that's where the
  //  reviewer sees "I need to do something".)
  // `pending (result )?entry` — the SAME state under two names: the legacy
  // QCBatchSequence enum says "Pending Result Entry" while Result Tracking and
  // the PLM rail say "Pending Entry". Only the long one matched, so one form
  // showed it amber and the next blue for identical data (owner report
  // 2026-09-25, marked against the PLM prototype, whose STATUS_PILL maps
  // 'Pending Entry' -> warn). "Pending Sample Prep" is the same story: the
  // analyst has not entered the layer either way.
  if (/(pending approval|pending signing|pending submit|pending (result )?entry|pending sample prep|waiting|in progress|in queue)/i.test(s))
    return 'warn';
  if (/(pending sampling|pending tier|pending for subout)/i.test(s))
    return 'warn';
  // QA/QC Training Records: "Pending QA Review" is waiting on a person, so it
  // belongs with the amber "needs your attention" group — not the blue
  // waiting-on-a-process one. Matched before the generic pending rules below.
  if (/pending qa review/i.test(s))
    return 'warn';
  // SDMS Result Validation/Approval: "Pending SDMS Validation" is amber in
  // ALPACA's list (analyst attention needed), not blue. Matched before the
  // generic "pending validation" blue rule below. (Merged 2026-07-07.)
  if (/pending sdms/i.test(s))
    return 'warn';

  // ── Pending validation / delivery — blue for "waiting on a process" ──
  if (/(pending validation|pending delivery|pending coa)/i.test(s))
    return 'info';
  if (/(pending receipt|pending transfer)/i.test(s))
    return 'info';

  // ── A master record's own state (Reference Library, 2026-10-08) ──────
  // Exact words only, so "Active" inside a longer status is not caught: an
  // Active record is usable (green); an Incomplete one needs someone to finish
  // it (amber). "Retired" stays neutral.
  if (s === 'active') return 'ok';
  if (s === 'incomplete') return 'warn';

  // ── My Jobs dashboard (2026-11-02): exact words only. Needs input = required
  // fields are missing; Ready = the login user can act on the job now.
  if (s === 'needs input') return 'alert';
  if (s === 'ready') return 'ok';

  // ── PLM Contamination Check station status (2026-10-16) ─────────────
  // Exact words only: OK = within the limit, Due soon = the last few layers
  // before a check is required, Check required = the limit is reached.
  if (s === 'ok') return 'ok';
  if (s === 'due soon') return 'warn';
  if (s === 'check required') return 'alert';

  // Default: blue / neutral
  return 'info';
}
